// @ts-nocheck
/**
 * Scheduling — "where do I start".
 *
 * The default destination for /admin-dashboard/events/:eventId/scheduling.
 * Prerequisites in order, live state for each, one primary action chosen by
 * that state. Nothing here is cached: a stale "0 courts" is worse than none.
 */
import * as React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2, AlertCircle, CircleDashed, ArrowRight, RefreshCw, Loader2,
} from 'lucide-react';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';

const STATE_ICON = {
  ok: CheckCircle2,
  partial: CircleDashed,
  missing: AlertCircle,
};

export default function OperatorEventSchedulingStartPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [data, setData] = React.useState<any>(null);
  const [check, setCheck] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: e } = await invokeEdgeFunction(
        'rle-operator-scheduling-readiness',
        { body: { action: 'get', event_id: eventId } },
      );
      if (e || !res?.ok) throw new Error(e?.message || res?.error?.message || 'Could not load');
      setData(res.data);
      if (res.data?.version_id) {
        // Errors and warnings come from the same check the publish button runs.
        const { data: c } = await invokeEdgeFunction('rle-operator-schedule-manual', {
          body: { action: 'publish_check', event_id: eventId },
        });
        if (c?.ok) setCheck(c.data);
      } else {
        setCheck(null);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Could not load the scheduling state');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  React.useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 p-6 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Reading this event…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={load}>Try again</Button>
      </div>
    );
  }
  if (!data) return null;

  const steps = data.steps ?? [];
  const missingCount = steps.filter((s: any) => s.state === 'missing').length;
  const errorCount = check?.error_count ?? 0;
  const warningCount = check?.warning_count ?? 0;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold md:text-2xl">Scheduling</h1>
          <p className="text-sm text-muted-foreground">
            {data.event?.name}
            {missingCount > 0
              ? ` — ${missingCount} thing${missingCount === 1 ? '' : 's'} to sort out before a schedule is possible.`
              : data.published
                ? ' — the schedule is live.'
                : ' — ready to work on the schedule.'}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* One primary action, chosen by state. */}
      <Card className="border-primary/40 bg-primary/5">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="min-w-0">
            <div className="text-sm font-medium">Start here</div>
            <div className="text-sm text-muted-foreground">{data.primary?.why}</div>
          </div>
          <Button onClick={() => navigate(data.primary.href)}>
            {data.primary?.label} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Before a schedule is possible</CardTitle>
        </CardHeader>
        <CardContent className="divide-y p-0">
          {steps.map((s: any) => {
            const Icon = STATE_ICON[s.state] ?? CircleDashed;
            const isFirstMissing = s.key === data.first_missing;
            return (
              <div
                key={s.key}
                className={`flex flex-wrap items-start gap-3 p-4 ${isFirstMissing ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}
              >
                <Icon
                  className={`mt-0.5 h-4 w-4 shrink-0 ${
                    s.state === 'ok'
                      ? 'text-emerald-600'
                      : s.state === 'missing'
                        ? 'text-destructive'
                        : 'text-amber-600'
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{s.label}</span>
                    {isFirstMissing && <Badge variant="outline">Start here</Badge>}
                  </div>
                  <div className="text-sm text-muted-foreground">{s.value}</div>
                  {s.blocks && (
                    <div className="mt-1 text-sm text-amber-700 dark:text-amber-400">{s.blocks}</div>
                  )}
                </div>
                {s.href && (
                  <Button variant="ghost" size="sm" onClick={() => navigate(s.href)}>
                    {s.href_label}
                  </Button>
                )}
              </div>
            );
          })}

          {/* Checks — same computation the publish button runs. */}
          <div className="flex flex-wrap items-start gap-3 p-4">
            {errorCount > 0
              ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">Checks</div>
              <div className="text-sm text-muted-foreground">
                {!data.version_id
                  ? 'Nothing to check until a draft exists.'
                  : `${errorCount} error${errorCount === 1 ? '' : 's'} · ${warningCount} warning${warningCount === 1 ? '' : 's'}`}
              </div>
              {errorCount > 0 && (
                <div className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                  Publishing is blocked until these are fixed.
                </div>
              )}
            </div>
            {data.version_id && (
              <Button variant="ghost" size="sm" onClick={() => navigate(`/admin-dashboard/events/${eventId}/scheduling/grid`)}>
                Grid
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-start gap-3 p-4">
            {data.published
              ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              : <CircleDashed className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">Published</div>
              <div className="text-sm text-muted-foreground">
                {data.published
                  ? 'Live on the public event page.'
                  : 'Not published — nobody outside this dashboard can see it yet.'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="text-sm text-muted-foreground">
        Know where you are going?{' '}
        <Link className="underline" to={`/admin-dashboard/events/${eventId}/scheduling/grid`}>Grid</Link>{' · '}
        <Link className="underline" to={`/admin-dashboard/events/${eventId}/scheduling/bulk-edit`}>Bulk edit</Link>{' · '}
        <Link className="underline" to={`/admin-dashboard/events/${eventId}/scheduling/snapshots`}>Save / Restore</Link>{' · '}
        <Link className="underline" to={`/admin-dashboard/events/${eventId}/scheduling-requests`}>Schedule Requests</Link>{' · '}
        <Link className="underline" to={`/admin-dashboard/events/${eventId}/schedule-setup`}>Schedule setup</Link>{' · '}
        <Link className="underline" to={`/admin-dashboard/events/${eventId}/print-pack`}>Print pack</Link>
      </div>
    </div>
  );
}
